import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminPreview } from '../../contexts/AdminPreviewContext';
import { exportEvaluationToPDF } from './pdfExport';
import { formatUserName } from '../../utils/industryConfig';

// Date formatter for PDF export
const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to convert categoryScores from object to array format
const getCategoryScoresArray = (categoryScores) => {
  if (!categoryScores) return [];
  if (Array.isArray(categoryScores)) return categoryScores;
  
  // Convert object to array
  return Object.keys(categoryScores).map(key => ({
    category: categoryScores[key].category || key,
    score: categoryScores[key].score || 0,
    maxScore: categoryScores[key].maxScore || 10
  }));
};

// Score grade helper
const getScoreGrade = (score) => {
  if (score >= 85) return { label: 'Excellent', color: 'text-green-600', bg: 'bg-green-100' };
  if (score >= 70) return { label: 'Good', color: 'text-blue-600', bg: 'bg-blue-100' };
  if (score >= 55) return { label: 'Average', color: 'text-yellow-600', bg: 'bg-yellow-100' };
  return { label: 'Needs Work', color: 'text-red-600', bg: 'bg-red-100' };
};

export default function Analytics({ evaluations, onViewReport }) {
  const { userId, userEmail } = useAuth();
  const { effectiveUserRole: userRole } = useAdminPreview();
  const [selectedEmail, setSelectedEmail] = useState(null); // Store email, not full object
  const [dateRange, setDateRange] = useState('30');

  // Filter evaluations by date range and get comparison period
  const { filteredEvaluations, comparisonEvaluations } = useMemo(() => {
    if (!evaluations) return { filteredEvaluations: [], comparisonEvaluations: [] };
    
    const completed = evaluations.filter(e => e.status === 'completed' && e.evaluation);
    
    if (dateRange === 'all') {
      return { filteredEvaluations: completed, comparisonEvaluations: [] };
    }
    
    const now = new Date();
    const days = {
      '7': 7,
      '30': 30,
      '90': 90
    }[dateRange];
    
    const currentPeriodStart = new Date(now - days * 24 * 60 * 60 * 1000);
    const previousPeriodStart = new Date(now - (days * 2) * 24 * 60 * 60 * 1000);
    
    const current = completed.filter(e => {
      const date = e.uploadedAt?.toDate ? e.uploadedAt.toDate() : new Date(e.uploadedAt);
      return date >= currentPeriodStart;
    });
    
    const previous = completed.filter(e => {
      const date = e.uploadedAt?.toDate ? e.uploadedAt.toDate() : new Date(e.uploadedAt);
      return date >= previousPeriodStart && date < currentPeriodStart;
    });
    
    return { filteredEvaluations: current, comparisonEvaluations: previous };
  }, [evaluations, dateRange]);

  // For agents, filter to show only their own evaluations
  const isBasicUser = userRole === 'basic';
  
  const displayEvaluations = useMemo(() => {
    if (isBasicUser && userEmail) {
      return filteredEvaluations.filter(e => 
        e.salesperson?.toLowerCase() === userEmail.toLowerCase()
      );
    }
    return filteredEvaluations;
  }, [filteredEvaluations, isBasicUser, userEmail]);

  const displayComparisonEvaluations = useMemo(() => {
    if (isBasicUser && userEmail) {
      return comparisonEvaluations.filter(e => 
        e.salesperson?.toLowerCase() === userEmail.toLowerCase()
      );
    }
    return comparisonEvaluations;
  }, [comparisonEvaluations, isBasicUser, userEmail]);

  // Calculate team statistics
  const teamStats = useMemo(() => {
    if (displayEvaluations.length === 0) return null;

    // Group by salesperson (current period)
    const byPerson = {};
    displayEvaluations.forEach(evaluation => {
      const email = evaluation.salesperson;
      if (!byPerson[email]) {
        byPerson[email] = {
          email,
          salespersonName: evaluation.salespersonName || null,
          calls: [],
          totalScore: 0,
          categoryTotals: Array(10).fill(0),
          highScore: 0,
          lowScore: 100
        };
      }
      if (!byPerson[email].salespersonName && evaluation.salespersonName) {
        byPerson[email].salespersonName = evaluation.salespersonName;
      }
      
      const score = evaluation.evaluation.overallScore || 0;
      byPerson[email].calls.push(evaluation);
      byPerson[email].totalScore += score;
      byPerson[email].highScore = Math.max(byPerson[email].highScore, score);
      byPerson[email].lowScore = Math.min(byPerson[email].lowScore, score);
      
      // Sum category scores
      const categoryScores = getCategoryScoresArray(evaluation.evaluation.categoryScores);
      categoryScores.forEach((cat, idx) => {
        byPerson[email].categoryTotals[idx] += cat.score || 0;
      });
    });

    // Group comparison period by salesperson
    const comparisonByPerson = {};
    displayComparisonEvaluations.forEach(evaluation => {
      const email = evaluation.salesperson;
      if (!comparisonByPerson[email]) {
        comparisonByPerson[email] = {
          totalScore: 0,
          callCount: 0
        };
      }
      comparisonByPerson[email].totalScore += evaluation.evaluation.overallScore || 0;
      comparisonByPerson[email].callCount += 1;
    });

    // Calculate averages and trends
    const salespeople = Object.values(byPerson).map(person => {
      const currentAvg = Math.round(person.totalScore / person.calls.length);
      
      // Calculate trend
      let trend = 0;
      if (comparisonByPerson[person.email] && comparisonByPerson[person.email].callCount > 0) {
        const previousAvg = Math.round(
          comparisonByPerson[person.email].totalScore / comparisonByPerson[person.email].callCount
        );
        trend = currentAvg - previousAvg;
      }

      return {
        email: person.email,
        name: (person.salespersonName && person.salespersonName !== person.email)
          ? person.salespersonName
          : formatUserName({ email: person.email }),
        callCount: person.calls.length,
        avgScore: currentAvg,
        highScore: person.highScore,
        lowScore: person.lowScore,
        categoryAvgs: person.categoryTotals.map(total => 
          Math.round(total / person.calls.length * 10) / 10
        ),
        recentCalls: person.calls.slice(-5).reverse(),
        trend: trend
      };
    });

    // Overall team stats
    const totalCalls = displayEvaluations.length;
    const avgScore = Math.round(
      displayEvaluations.reduce((sum, e) => sum + (e.evaluation.overallScore || 0), 0) / totalCalls
    );
    
    // Calculate previous period average for team trend
    let teamTrend = 0;
    if (displayComparisonEvaluations.length > 0) {
      const prevAvg = Math.round(
        displayComparisonEvaluations.reduce((sum, e) => sum + (e.evaluation.overallScore || 0), 0) / displayComparisonEvaluations.length
      );
      teamTrend = avgScore - prevAvg;
    }

    const sortedSalespeople = salespeople.sort((a, b) => b.avgScore - a.avgScore);

    return {
      totalCalls,
      avgScore,
      teamTrend,
      salespeople: sortedSalespeople,
      topPerformer: sortedSalespeople[0] || null
    };
  }, [displayEvaluations, displayComparisonEvaluations]);

  // Check if there are any completed evaluations at all (regardless of date range)
  const hasAnyCompletedEvals = useMemo(() => {
    if (!evaluations) return false;
    return evaluations.some(e => e.status === 'completed' && e.evaluation);
  }, [evaluations]);

  if (!teamStats) {
    return (
      <div className="space-y-4">
        {/* Still show header + date filter so user can change the range */}
        {hasAnyCompletedEvals && (
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">🏆 Team Performance</h2>
              <p className="text-sm text-gray-500 mt-1">Track and compare team member scores</p>
            </div>
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { value: '7', label: '7 Days' },
                { value: '30', label: '30 Days' },
                { value: '90', label: '90 Days' },
                { value: 'all', label: 'All Time' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setDateRange(value)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    dateRange === value
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="text-center py-8">
          <div className="text-6xl mb-3">📊</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No data yet</h3>
          <p className="text-gray-500">
            {isBasicUser 
              ? 'You don\'t have any call evaluations yet.' 
              : hasAnyCompletedEvals
                ? `No call evaluations in the last ${dateRange === 'all' ? '' : dateRange + ' days'}. Try a different time period.`
                : 'No call evaluations available yet.'}
          </p>
          {hasAnyCompletedEvals && dateRange !== 'all' && (
            <button
              onClick={() => setDateRange('all')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Show All Time
            </button>
          )}
        </div>
      </div>
    );
  }

  // Find the selected salesperson from the recalculated teamStats
  const selectedSalesperson = selectedEmail 
    ? teamStats.salespeople.find(p => p.email === selectedEmail) 
    : null;

  // If agent or individual selected, show individual view
  if (isBasicUser || selectedSalesperson) {
    return (
      <IndividualView
        salesperson={isBasicUser ? teamStats.salespeople[0] : selectedSalesperson}
        dateRange={dateRange}
        setDateRange={setDateRange}
        onBack={isBasicUser ? null : () => setSelectedEmail(null)}
        isBasicUser={isBasicUser}
        onViewReport={onViewReport}
      />
    );
  }

  // If we had a selection but that person has no data in current range, show message
  if (selectedEmail && !selectedSalesperson) {
    return (
      <div className="text-center py-8">
        <div className="text-6xl mb-3">📊</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">No data for this period</h3>
        <p className="text-gray-500 mb-4">
          This person has no call evaluations in the selected time period.
        </p>
        <button
          onClick={() => setSelectedEmail(null)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          ← Back to Team
        </button>
      </div>
    );
  }

  // Team Performance Dashboard (for team leads and admins)
  return (
    <div className="space-y-4">
      {/* Header with Date Range Filter */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🏆 Team Performance</h2>
          <p className="text-sm text-gray-500 mt-1">Track and compare team member scores</p>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-1">
          {[
            { value: '7', label: '7 Days' },
            { value: '30', label: '30 Days' },
            { value: '90', label: '90 Days' },
            { value: 'all', label: 'All Time' }
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setDateRange(value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                dateRange === value
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Team Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Calls</p>
              <p className="text-4xl font-bold mt-1">{teamStats.totalCalls}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Team Average</p>
              <p className="text-4xl font-bold mt-1">{teamStats.avgScore}<span className="text-xl text-green-200">/100</span></p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm font-medium">Top Performer</p>
              <p className="text-2xl font-bold mt-1 truncate" title={teamStats.topPerformer?.name || 'N/A'}>{teamStats.topPerformer?.name || 'N/A'}</p>
              <p className="text-amber-200 text-sm">{teamStats.topPerformer?.avgScore}/100 avg</p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <span className="text-3xl">🏆</span>
            </div>
          </div>
        </div>

        <div className={`bg-gradient-to-br ${teamStats.teamTrend >= 0 ? 'from-emerald-500 to-emerald-600' : 'from-red-500 to-red-600'} rounded-xl p-5 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium">Trend</p>
              <p className="text-4xl font-bold mt-1">
                {teamStats.teamTrend > 0 ? '+' : ''}{teamStats.teamTrend || '—'}
              </p>
              <p className="text-white/70 text-sm">
                {dateRange === 'all' ? 'Select period' : `vs previous ${dateRange} days`}
              </p>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              {teamStats.teamTrend >= 0 ? (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              ) : (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">🏅 Leaderboard</h3>
        </div>
        
        <div className="divide-y divide-gray-100">
          {teamStats.salespeople.map((person, idx) => {
            const rank = idx + 1;
            const isTopThree = rank <= 3;
            const grade = getScoreGrade(person.avgScore);
            
            // Rank styling
            const getRankStyle = () => {
              if (rank === 1) return 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white';
              if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-700';
              if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white';
              return 'bg-gray-100 text-gray-600';
            };

            const getRankEmoji = () => {
              if (rank === 1) return '🥇';
              if (rank === 2) return '🥈';
              if (rank === 3) return '🥉';
              return rank;
            };

            return (
              <div 
                key={person.email} 
                className={`flex items-center px-4 py-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                  isTopThree ? 'bg-gradient-to-r from-amber-50/50 to-transparent' : ''
                }`}
                onClick={() => setSelectedEmail(person.email)}
              >
                {/* Rank */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${getRankStyle()}`}>
                  {getRankEmoji()}
                </div>

                {/* Agent Info */}
                <div className="ml-4 flex-1 min-w-0">
                  <div className="flex items-center">
                    <p className={`font-semibold truncate ${isTopThree ? 'text-lg' : 'text-base'} text-gray-900`} title={person.name}>
                      {person.name}
                    </p>
                    {person.trend > 0 && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ↑ {person.trend}
                      </span>
                    )}
                    {person.trend < 0 && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        ↓ {Math.abs(person.trend)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    {person.callCount} calls • {person.highScore} high • {person.lowScore} low
                  </p>
                </div>

                {/* Score */}
                <div className="text-right mr-4">
                  <div className={`text-3xl font-bold ${grade.color}`}>
                    {person.avgScore}
                  </div>
                  <div className={`text-xs font-medium ${grade.color} ${grade.bg} px-2 py-0.5 rounded-full inline-block`}>
                    {grade.label}
                  </div>
                </div>

                {/* View Button */}
                <button className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors" aria-label={`View details for ${person.name}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Insights */}
      <div className="grid grid-cols-2 gap-4">
        {/* Strongest Categories */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">💪 Strongest Categories (Team Avg)</h3>
          <TeamCategoryBreakdown evaluations={displayEvaluations} limit={5} sortOrder="desc" />
        </div>

        {/* Needs Improvement */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">📈 Needs Improvement (Team Avg)</h3>
          <TeamCategoryBreakdown evaluations={displayEvaluations} limit={5} sortOrder="asc" />
        </div>
      </div>
    </div>
  );
}

// Team Category Breakdown Component
function TeamCategoryBreakdown({ evaluations, limit = 5, sortOrder = 'desc' }) {
  const categoryNames = [
    'Opening & Control',
    'Empathy & Curiosity',
    'Emotional Engagement',
    'Non-Neediness',
    'Stories vs Explaining',
    'Assertive vs Aggressive',
    'Guiding Self-Sell',
    'Objection Handling',
    'Closing & Buy-In',
    'Next Steps'
  ];

  const categoryAverages = useMemo(() => {
    const totals = Array(10).fill(0);
    const counts = Array(10).fill(0);

    evaluations.forEach(evaluation => {
      const categoryScores = getCategoryScoresArray(evaluation.evaluation.categoryScores);
      categoryScores.forEach((cat, idx) => {
        if (cat.score !== null && cat.score !== undefined) {
          totals[idx] += cat.score;
          counts[idx] += 1;
        }
      });
    });

    return categoryNames.map((name, idx) => ({
      name,
      avg: counts[idx] > 0 ? Math.round(totals[idx] / counts[idx] * 10) / 10 : 0
    }))
      .sort((a, b) => sortOrder === 'desc' ? b.avg - a.avg : a.avg - b.avg)
      .slice(0, limit);
  }, [evaluations, sortOrder, limit]);

  const getBarColor = (avg) => {
    if (avg >= 8) return 'bg-green-500';
    if (avg >= 6) return 'bg-blue-500';
    if (avg >= 4) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-3">
      {categoryAverages.map((cat, idx) => (
        <div key={idx}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-700 truncate" title={cat.name}>{cat.name}</span>
            <span className="font-semibold text-gray-900">{cat.avg}/10</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getBarColor(cat.avg)} transition-all`}
              style={{ width: `${(cat.avg / 10) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// Individual salesperson view component
function IndividualView({ salesperson, dateRange, setDateRange, onBack, isBasicUser, onViewReport }) {
  if (!salesperson) return null;

  const categoryNames = [
    'Opening & Control',
    'Empathy & Curiosity',
    'Emotional Engagement',
    'Non-Neediness',
    'Stories vs Explaining',
    'Assertive vs Aggressive',
    'Guiding Self-Sell',
    'Objection Handling',
    'Closing & Buy-In',
    'Next Steps'
  ];

  const grade = getScoreGrade(salesperson.avgScore);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="text-blue-600 hover:text-blue-800 flex items-center font-medium"
            >
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Team
            </button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isBasicUser ? '📊 Your Performance' : `📊 ${salesperson.name}'s Performance`}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{salesperson.callCount} calls analyzed</p>
          </div>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-1">
          {[
            { value: '7', label: '7 Days' },
            { value: '30', label: '30 Days' },
            { value: '90', label: '90 Days' },
            { value: 'all', label: 'All Time' }
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setDateRange(value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                dateRange === value
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500 font-medium">Average Score</p>
          <div className="flex items-baseline mt-1">
            <p className={`text-4xl font-bold ${grade.color}`}>{salesperson.avgScore}</p>
            <span className="text-lg text-gray-400 ml-1">/100</span>
          </div>
          <span className={`text-xs font-medium ${grade.color} ${grade.bg} px-2 py-0.5 rounded-full inline-block mt-2`}>
            {grade.label}
          </span>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500 font-medium">Total Calls</p>
          <p className="text-4xl font-bold text-gray-900 mt-1">{salesperson.callCount}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500 font-medium">Score Range</p>
          <div className="flex items-center mt-1 space-x-2">
            <span className="text-2xl font-bold text-red-500">{salesperson.lowScore}</span>
            <span className="text-gray-400">→</span>
            <span className="text-2xl font-bold text-green-500">{salesperson.highScore}</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Low to High</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500 font-medium">Trend</p>
          <p className={`text-4xl font-bold mt-1 ${
            salesperson.trend > 0 ? 'text-green-600' : 
              salesperson.trend < 0 ? 'text-red-600' : 'text-gray-400'
          }`}>
            {salesperson.trend > 0 ? '+' : ''}{salesperson.trend || '—'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {salesperson.trend > 0 ? 'Improving! 🎉' : 
              salesperson.trend < 0 ? 'Needs focus' : 
                dateRange === 'all' ? 'Select period' : 'No prior data'}
          </p>
        </div>
      </div>

      {/* Category Performance */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 Category Performance</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          {salesperson.categoryAvgs.map((avg, idx) => {
            const getBarColor = () => {
              if (avg >= 8) return 'bg-green-500';
              if (avg >= 6) return 'bg-blue-500';
              if (avg >= 4) return 'bg-yellow-500';
              return 'bg-red-500';
            };

            return (
              <div key={idx}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{categoryNames[idx]}</span>
                  <span className={`font-bold ${
                    avg >= 8 ? 'text-green-600' : 
                      avg >= 6 ? 'text-blue-600' : 
                        avg >= 4 ? 'text-yellow-600' : 'text-red-600'
                  }`}>{avg}/10</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${getBarColor()} transition-all`}
                    style={{ width: `${(avg / 10) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Calls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">📞 Recent Calls</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {salesperson.recentCalls.map((call) => {
            const callGrade = getScoreGrade(call.evaluation.overallScore);
            const date = call.uploadedAt?.toDate ? call.uploadedAt.toDate() : new Date(call.uploadedAt);
            
            return (
              <div key={call.id} className="flex items-center px-4 py-4 hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate" title={call.prospect || 'Unknown Prospect'}>{call.prospect || 'Unknown Prospect'}</p>
                  <p className="text-sm text-gray-500">
                    {date.toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                
                {/* Score */}
                <div className="text-right mx-4">
                  <span className={`text-2xl font-bold ${callGrade.color}`}>
                    {call.evaluation.overallScore}
                  </span>
                  <span className="text-sm text-gray-400">/100</span>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2">
                  {onViewReport && (
                    <button
                      onClick={() => onViewReport(call)}
                      className="flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      title="View Report"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      View
                    </button>
                  )}
                  <button
                    onClick={() => exportEvaluationToPDF(call, formatDate)}
                    className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Download PDF"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    PDF
                  </button>
                </div>
              </div>
            );
          })}
          {salesperson.recentCalls.length === 0 && (
            <div className="px-4 py-4 text-center text-gray-500">
              No calls in this time period
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
